'use client'

import { useState } from 'react';
import { Globe, Mail } from 'lucide-react';
import { formatFontFamily } from '@/lib/themeHelpers'
import { useScrollAnimation, scrollAnimationClasses } from '@/lib/useScrollAnimation'

interface InventorySectionProps {
  surface: "dark" | "light";
  theme: any;
  radius: number;
  fontFamily: string;
  gradStart: string;
  gradEnd: string;
  angle: number;
  content: any;
}

interface PricingRow {
  adType: string;
  size: string;
  week1: string;
  week4: string;
  week8: string;
  week12: string;
  week26: string;
  highlight?: 'yellow' | 'blue';
}

interface NewsletterPricingRow {
  newsletter: string;
  banner: string;
  leaderboard: string;
}

const tabsData = [
  {
    id: 'windycitytimes',
    label: 'WindyCityTimes.com',
    icon: Globe,
    data: [
      { adType: 'Billboard', size: '970 x 250 pixels', week1: '$300', week4: '$1000', week8: '$1,800', week12: '$2,550', week26: '$5,850', highlight: 'yellow' as const },
      { adType: 'Leaderboard', size: '728 x 90 pixels', week1: '$250', week4: '$800', week8: '$1,440', week12: '$2,040', week26: '$4,680', highlight: 'yellow' as const },
      { adType: 'Med. Rectangle', size: '300 x 250 pixels', week1: '$200', week4: '$500', week8: '$900', week12: '$1,275', week26: '$2,925', highlight: 'yellow' as const },
      { adType: 'Filmstrip (MPU)', size: '300 x 600 pixels', week1: '$400', week4: '$700', week8: '$1,260', week12: '$1,785', week26: '$4,095', highlight: 'yellow' as const },
      { adType: 'Social Media**', size: '', week1: '$100', week4: '$350', week8: '$600', week12: '', week26: '', highlight: 'blue' as const },
    ]
  },
  {
    id: 'chicagosocialbutterflies',
    label: 'ChicagoSocialButterflies.com',
    icon: Globe,
    data: [
      { adType: 'Billboard', size: '970 x 250 pixels', week1: '$150', week4: '$500', week8: '$884', week12: '$1,105', week26: '$2,026', highlight: 'yellow' as const },
      { adType: 'Leaderboard', size: '728 x 90 pixels', week1: '$125', week4: '$400', week8: '$708', week12: '$884', week26: '$1,612', highlight: 'yellow' as const },
      { adType: 'Med. Rectangle', size: '300 x 250 pixels', week1: '$100', week4: '$320', week8: '$566', week12: '$707', week26: '$1,290', highlight: 'yellow' as const },
      { adType: 'Filmstrip (MPU)', size: '300 x 600 pixels', week1: '$200', week4: '$440', week8: '$778', week12: '$973', week26: '$1,778', highlight: 'yellow' as const },
      { adType: 'Social Media**', size: '', week1: '$75', week4: '$275', week8: '$475', week12: '', week26: '', highlight: 'blue' as const },
    ]
  },
  {
    id: 'newsletters',
    label: 'Email Newsletters',
    icon: Mail,
    isNewsletter: true,
    newsletterData: [
      { newsletter: 'News on Q', banner: '$200', leaderboard: '$150' },
      { newsletter: 'CSB Newsletter', banner: '$150', leaderboard: '$100' },
      { newsletter: 'BLACKlines', banner: '$100', leaderboard: '$75' },
      { newsletter: 'En La Vida', banner: '$100', leaderboard: '$75' },
    ]
  }
];

function PricingTable({ 
  data, 
  theme, 
}: { 
  data: PricingRow[]; 
  theme: any; 
}) {
  return (
    <div className="overflow-x-auto">
      <table className="w-full border-collapse">
        <thead>
          <tr>
            <th className="text-left py-2 px-3 text-sm font-semibold font-primary" style={{ color: theme.text, borderBottom: `2px solid ${theme.border}` }}></th>
            <th className="text-center py-2 px-3 text-sm font-semibold font-primary" style={{ color: theme.text, borderBottom: `2px solid ${theme.border}` }}>1-week</th>
            <th className="text-center py-2 px-3 text-sm font-semibold font-primary" style={{ color: theme.text, borderBottom: `2px solid ${theme.border}` }}>4-weeks</th>
            <th className="text-center py-2 px-3 text-sm font-semibold font-primary" style={{ color: theme.text, borderBottom: `2px solid ${theme.border}` }}>8-weeks</th>
            <th className="text-center py-2 px-3 text-sm font-semibold font-primary" style={{ color: theme.text, borderBottom: `2px solid ${theme.border}` }}>12-weeks</th>
            <th className="text-center py-2 px-3 text-sm font-semibold font-primary" style={{ color: theme.text, borderBottom: `2px solid ${theme.border}` }}>26-weeks</th>
          </tr>
        </thead>
        <tbody>
          {data.map((row, index) => (
            <tr 
              key={index}
              className="transition-colors duration-200 cursor-default"
              onMouseEnter={(e) => e.currentTarget.style.backgroundColor = theme.surface === 'dark' ? 'rgba(255,255,255,0.03)' : 'rgba(0,0,0,0.02)'}
              onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
            >
              <td className="py-2 px-3" style={{ borderBottom: `1px solid ${theme.border}` }}>
                <div className="text-sm font-semibold font-primary" style={{ color: theme.text }}>{row.adType}</div>
                {row.size && (
                  <div 
                    className="text-xs px-1.5 py-0.5 mt-1 inline-block rounded font-primary font-bold"
                    style={{ 
                      backgroundColor: '#1e3a5f',
                      color: '#fff'
                    }}
                  >
                    {row.size}
                  </div>
                )}
              </td>
              <td className="py-2 px-3 text-center text-sm font-semibold font-primary" style={{ color: theme.text, borderBottom: `1px solid ${theme.border}` }}>{row.week1}</td>
              <td className="py-2 px-3 text-center text-sm font-semibold font-primary" style={{ color: theme.text, borderBottom: `1px solid ${theme.border}` }}>{row.week4}</td>
              <td className="py-2 px-3 text-center text-sm font-semibold font-primary" style={{ color: theme.text, borderBottom: `1px solid ${theme.border}` }}>{row.week8}</td>
              <td className="py-2 px-3 text-center text-sm font-semibold font-primary" style={{ color: theme.text, borderBottom: `1px solid ${theme.border}` }}>{row.week12 || '-'}</td>
              <td className="py-2 px-3 text-center text-sm font-semibold font-primary" style={{ color: theme.text, borderBottom: `1px solid ${theme.border}` }}>{row.week26 || '-'}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function NewsletterPricingTable({ 
  data, 
  theme, 
}: { 
  data: NewsletterPricingRow[]; 
  theme: any; 
}) {
  return (
    <div className="overflow-x-auto">
      <table className="w-full border-collapse">
        <thead>
          <tr>
            <th className="text-left py-2 px-3 text-sm font-semibold font-primary" style={{ color: theme.text, borderBottom: `2px solid ${theme.border}` }}>Newsletter</th>
            <th className="text-center py-2 px-3 text-sm font-semibold font-primary" style={{ color: theme.text, borderBottom: `2px solid ${theme.border}` }}>
              <div>Banner</div>
              <div className="text-xs font-normal" style={{ color: theme.mutedText }}>300×250 / per issue</div>
            </th>
            <th className="text-center py-2 px-3 text-sm font-semibold font-primary" style={{ color: theme.text, borderBottom: `2px solid ${theme.border}` }}>
              <div>Leaderboard</div>
              <div className="text-xs font-normal" style={{ color: theme.mutedText }}>600×100 / per issue</div>
            </th>
          </tr>
        </thead>
        <tbody>
          {data.map((row, index) => (
            <tr 
              key={index}
              className="transition-colors duration-200 cursor-default"
              onMouseEnter={(e) => e.currentTarget.style.backgroundColor = theme.surface === 'dark' ? 'rgba(255,255,255,0.03)' : 'rgba(0,0,0,0.02)'}
              onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
            >
              <td className="py-3 px-3" style={{ borderBottom: `1px solid ${theme.border}` }}>
                <div className="text-sm font-semibold font-primary" style={{ color: theme.text }}>{row.newsletter}</div>
              </td>
              <td className="py-3 px-3 text-center text-sm font-semibold font-primary" style={{ color: theme.text, borderBottom: `1px solid ${theme.border}` }}>{row.banner}</td>
              <td className="py-3 px-3 text-center text-sm font-semibold font-primary" style={{ color: theme.text, borderBottom: `1px solid ${theme.border}` }}>{row.leaderboard}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export default function InventorySection({ 
  surface, 
  theme, 
  radius, 
  fontFamily, 
  gradStart, 
  gradEnd, 
  angle,
  content
}: InventorySectionProps) {
  const [activeTab, setActiveTab] = useState('windycitytimes');
  const { ref: headerRef, isVisible: headerVisible } = useScrollAnimation();
  const { ref: tableRef, isVisible: tableVisible } = useScrollAnimation({ rootMargin: '0px 0px -50px 0px' });

  const backgroundStyle = {
    backgroundImage: surface === "dark" 
      ? `linear-gradient(${angle}deg, ${gradStart}, ${gradEnd})`
      : `linear-gradient(${angle}deg, #f8f9fa, #e9ecef)`,
    fontFamily: formatFontFamily(fontFamily)
  };

  const activeTabData = tabsData.find(tab => tab.id === activeTab);

  return (
    <section id="inventory" className="relative w-full py-16" style={backgroundStyle}>
      <div className="mx-auto max-w-6xl px-6 lg:px-8">
        {/* Header */}
        <div 
          ref={headerRef as React.RefObject<HTMLDivElement>}
          className={`text-center mb-10 ${scrollAnimationClasses.transition} ${headerVisible ? scrollAnimationClasses.visible : scrollAnimationClasses.hidden}`}
        >
          <h2 className="text-2xl md:text-3xl lg:text-4xl font-bold mb-4 leading-tight font-primary" style={{ color: theme.text }}>
            {content.title}
          </h2>
          <p className="text-lg max-w-3xl mx-auto font-secondary" style={{ color: theme.mutedText }}>
            {content.description}
          </p>
        </div>

        {/* Tabs */}
        <div 
          ref={tableRef as React.RefObject<HTMLDivElement>}
          className={`${scrollAnimationClasses.transition} ${tableVisible ? scrollAnimationClasses.visible : scrollAnimationClasses.hidden}`}
          style={{ transitionDelay: '100ms' }}
        >
        <div className="flex flex-wrap justify-center gap-2 mb-6">
          {tabsData.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className="px-4 py-2 text-sm transition-all duration-200 font-primary flex items-center hover:scale-[1.02] hover:-translate-y-0.5"
              style={{
                background: activeTab === tab.id ? '#E8EDF5' : '#ffffff',
                color: activeTab === tab.id ? '#103065' : '#6b7280',
                fontWeight: activeTab === tab.id ? '600' : '500',
                borderRadius: '9999px',
                border: activeTab === tab.id ? '1px solid #103065' : '1px solid #e5e7eb',
                boxShadow: activeTab === tab.id ? 'none' : '0 1px 2px rgba(0,0,0,0.05)'
              }}
              onMouseEnter={(e) => {
                if (activeTab !== tab.id) {
                  e.currentTarget.style.borderColor = '#103065';
                  e.currentTarget.style.boxShadow = '0 4px 12px rgba(16, 48, 101, 0.15)';
                }
              }}
              onMouseLeave={(e) => {
                if (activeTab !== tab.id) {
                  e.currentTarget.style.borderColor = '#e5e7eb';
                  e.currentTarget.style.boxShadow = '0 1px 2px rgba(0,0,0,0.05)';
                }
              }}
            >
              <tab.icon size={16} className="mr-2" />
              {tab.label}
            </button>
          ))}
        </div>

        {/* Pricing Table */}
        <div 
          className="p-5 shadow-sm transition-all duration-300 hover:-translate-y-1 cursor-default"
          style={{
            backgroundColor: surface === 'dark' ? 'rgba(255,255,255,0.05)' : '#fff',
            border: `1px solid ${theme.border}`,
            borderRadius: radius
          }}
          onMouseEnter={(e) => e.currentTarget.style.boxShadow = '0 8px 25px rgba(0, 0, 0, 0.15)'}
          onMouseLeave={(e) => e.currentTarget.style.boxShadow = 'none'}
        >
          {activeTabData && (
            activeTabData.isNewsletter ? (
              <NewsletterPricingTable 
                data={activeTabData.newsletterData!} 
                theme={theme} 
              />
            ) : (
              <PricingTable 
                data={activeTabData.data!} 
                theme={theme} 
              />
            )
          )}
          
          {!activeTabData?.isNewsletter && (
            <p className="text-sm mt-4 font-primary" style={{ color: theme.mutedText }}>
              ** Social Media pricing includes posts on our social media channels promoting your brand or event.
            </p>
          )}
        </div>
        </div>
      </div>
    </section>
  );
}
